import { TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { PlaceholderContainer } from '@topcon/topcon-utils';
import { useMemo, useState } from 'react';

import { Section } from '../../utils/styling';
import EmptyDivisions from './EmptyDivisions';
import ViewContainer from '../../components/ViewContainer';
import { useAppService } from '../../providers/app/app.context';
import { DIVISIONS_CREATED_LIMIT } from '../../app/constants';
import CompanyDivisionModal from '../../components/Modal/CompanyDivisionModal';
import LimitReachedModal from '../../components/Modal/LimitReachedModal';
import CompanyTree from '../../components/Company/CompanyTree';
import { useCanCUDDivisions } from '../../app/routes/utils';
import { useAggregateDivisions } from '../../hooks/useAggregateDivisions';

function Divisions() {
  const { t } = useTranslation('divisions');
  const { identity, loadedIdentity } = useAppService();
  const canCUDDivisions = useCanCUDDivisions();
  const { divisions: combinedDivisions, isLoading } = useAggregateDivisions();
  const [searchValue, setSearchValue] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);

  const createdDivisionsCount = combinedDivisions.filter(
    (division) => division?.ownerIdentityId === identity?.id
  ).length;

  const filteredDivisions = useMemo(() => {
    return combinedDivisions.filter(({ name = '' }) => {
      const searchMatches = name.toLowerCase().includes(searchValue.toLowerCase());
      return searchMatches;
    });
  }, [combinedDivisions, searchValue]);

  const divisionsCount = combinedDivisions.length;
  const noContent = isLoading || !loadedIdentity || !filteredDivisions.length;

  const openCreateModal = () => setCreateModalOpen(true);

  const onCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const createNewItemClick = () => {
    if (canCUDDivisions) {
      return createdDivisionsCount >= DIVISIONS_CREATED_LIMIT
        ? () => setLimitModalOpen(true)
        : openCreateModal;
    } else {
      return undefined;
    }
  };

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('loadingDivisions')} />;
  };

  const renderNoDivisions = () => {
    return <EmptyDivisions onClick={createNewItemClick()} />;
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        fill
        justifyContent="center"
        alignItems="center"
        data-testid="divisions-no-content-test"
      >
        {isLoading || !loadedIdentity ? (
          renderLoader()
        ) : !divisionsCount ? (
          renderNoDivisions()
        ) : (
          <PlaceholderContainer text={t('noSearchResults')} />
        )}
      </TeContainerSlim>
    );
  };

  return (
    <TeContainerSlim
      column
      grow
      padding="xs"
      overflow="auto"
      alignItems="center"
      data-testid="divisions-container"
    >
      <Section>
        <ViewContainer
          title={t('divisions')}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          visibleItemsText={
            divisionsCount
              ? t('visibleDivisions', {
                  filteredDivisions: filteredDivisions.length,
                  divisionsCount,
                })
              : undefined
          }
          searchPlaceholder={t('searchPlaceholder')}
          createNewItemText={canCUDDivisions ? t('createDivision') : undefined}
          onCreateNewItemClick={createNewItemClick()}
          buttonDisabled={isLoading}
          noPadding
        >
          <TeContainer
            backgroundColor="background.primary"
            overflow="hidden"
            borderRadius={4}
            fill
            column
          >
            {noContent ? (
              renderNoContent()
            ) : (
              <CompanyTree type="divisions" companies={filteredDivisions} allowSignIn showRoles />
            )}
          </TeContainer>
        </ViewContainer>
      </Section>

      {createModalOpen && <CompanyDivisionModal open isDivision onClose={onCreateModalClose} />}

      <LimitReachedModal
        title={t('divisionLimitReached')}
        description={t('divisionLimitReachedDescription', { max: DIVISIONS_CREATED_LIMIT })}
        icon="business"
        open={limitModalOpen}
        setOpen={setLimitModalOpen}
      />
    </TeContainerSlim>
  );
}

export default Divisions;
